<template>
  <div>
    <sidenav :is-sidebar-active.sync="isSidebarActive" :item-edit.sync="itemEdit" :is-add.sync="isAdd"
      @refetch-data="refetchData" :permissions-options="permissionsOptionsComplete" :layouts-options="layoutsOptions" />

    <b-modal v-model="isCloneModalActive" :title="$t('profiles.clone_title')" :ok-title="$t('actions.clone')"
      :cancel-title="$t('actions.cancel')" :ok-disabled="!cloneName.trim() || isCloning" ok-variant="primary"
      cancel-variant="outline-secondary" @ok="cloneProfile" @hidden="resetCloneModal">
      <b-form-group :label="$t('profiles.new_name')" label-for="clone-profile-name">
        <b-form-input id="clone-profile-name" v-model="cloneName" trim autofocus :disabled="isCloning"
          data-cy="cloneProfileName" @keyup.enter="cloneProfile" />
      </b-form-group>
    </b-modal>
    <b-row class="pt-2 pb-1">
      <b-col cols="6">
        <BreadcrumbMenu />
      </b-col>
    </b-row>
    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />

    <!-- Filters -->
    <!-- <filters
      :permissions-filter.sync="permissionsFilter"
      :permissions-options="permissionsOptions"
    /> -->
    <!-- Table Container Card -->
    <b-card class="mx-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mx-50" data-cy="per-page-selector" />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block mr-1" :placeholder="$t('table.search')"
                data-cy="search" />
              <b-button variant="primary" @click="openAdd()" v-if="$can('create', 'Profile')" data-cy="addProfilenBtn">
                <span class="text-nowrap text-capitalize">{{
                  `${$t("actions.add")} ${$tc("profiles.title", 0)}`
                }}</span>
              </b-button>
              <b-button @click="exportExcel()" variant="success" class="text-capitalize ml-1" type="button"
                :disabled="buttonExport">
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table ref="refListTable" class="position-relative" :items="fetchList" responsive :fields="tableColumns"
        primary-key="id" :sort-by.sync="sortBy" show-empty :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc">
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template> 

        <template #cell(description)="data">
          <p v-b-tooltip.hover.topleft :title="data.item.description" style="max-width: 300px;"> {{
            data.item.description }}</p>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" data-cy="actions" />
            </template>

            <b-dropdown-item @click="openEdit(data.item)" v-if="$can('update', 'Profile')">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.edit")
              }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="openClone(data.item)" v-if="$can('create', 'Profile')">
              <feather-icon icon="CopyIcon" />
              <span class="align-middle ml-50 text-capitalize">{{ $t("actions.clone") }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="confirmDelete(data.item.id)" v-if="$can('delete', 'Profile')">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.delete")
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <Pagination :totalList="totalList" :currentPage="currentPage" @updatePagination="update" :perPage="perPage"
              data-cy="pagination">
            </Pagination>
          </b-col>
        </b-row>
      </div>

    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BModal,
  BFormGroup,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ToastificationLoadingContent
  from "@core/components/toastification/ToastificationLoadingContent.vue";
import Pagination from "@/components/Pagination.vue";

export default {
  components: {
    BreadcrumbMenu,
    ToastificationLoadingContent,
    Sidenav,
    Filters,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BFormGroup,
    BPagination,
    vSelect,
    Pagination
  },

  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    // start export
    const loadingToast = ref(null);
    const action = ref("list");
    const { t } = useI18nUtils();
    const trans = {
      "messages.export.download": t("messages.export.download"),
      "messages.export.Generating_file": t("messages.export.Generating_file"),
      "messages.error.server_error": t("messages.error.server_error"),
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "actions.clone": t("actions.clone"),
      "profiles.clone_success": t("profiles.clone_success"),
    };
    const exportExcel = () => {
      action.value == "export";
      buttonExport.value = true;
      loadingToast.value.show(
        trans["messages.export.download"],
        trans["messages.export.Generating_file"],
        "secondary"
      );
      store
        .dispatch("profile/export", {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          id_permission: permissionsFilter.value,
          action: "export",
        })
        .then((response) => {
          console.log(response);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.error.server_error"],
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          loadingToast.value.close();
          buttonExport.value = false;
        });
    };
    const buttonExport = ref(false);
    // end export

    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const isCloneModalActive = ref(false);
    const cloneSource = ref(null);
    const cloneName = ref("");
    const isCloning = ref(false);

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
    };

    const toast = useToast();

    const resetCloneModal = () => {
      cloneSource.value = null;
      cloneName.value = "";
      isCloning.value = false;
    };

    const openClone = (item) => {
      cloneSource.value = item;
      cloneName.value = `${item.type} - ${trans["actions.clone"]}`;
      isCloneModalActive.value = true;
    };

    const relationIds = (items) => (items || []).map(item =>
      typeof item === "object" ? item.id : item
    );

    const cloneProfile = (event) => {
      if (event && event.preventDefault) event.preventDefault();
      if (!cloneName.value.trim() || !cloneSource.value || isCloning.value) return;

      const source = cloneSource.value;
      isCloning.value = true;
      store.dispatch("profile/add", {
        type: cloneName.value.trim(),
        description: source.description,
        permissions: relationIds(source.permissions),
        layouts: relationIds(source.layouts),
        forced_layouts: source.forced_layouts,
        token_lifetime: source.token_lifetime,
        priority: source.priority,
        inactivity_lifetime: source.inactivity_lifetime,
      }).then((response) => {
        if (!response.success) throw new Error(response.message);
        toast({ component: ToastificationContent, props: {
          title: response.message || trans["profiles.clone_success"], icon: "CheckIcon", variant: "success",
        }});
        isCloneModalActive.value = false;
        refetchData();
      }).catch((error) => {
        toast({ component: ToastificationContent, props: {
          title: error.response?.data?.message || error.message || trans["messages.error.server_error"],
          icon: "AlertTriangleIcon", variant: "danger",
        }}, { timeout: 10000 });
      }).finally(() => {
        isCloning.value = false;
      });
    };

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,

      permissionsOptions,
      permissionsOptionsComplete,
      permissionsFilter,

      layoutsOptions,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("profile/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    return {
      loadingToast,
      action,
      exportExcel,
      buttonExport,
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      isCloneModalActive,
      cloneName,
      isCloning,
      openClone,
      cloneProfile,
      resetCloneModal,
      confirmDelete,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      permissionsOptions,
      permissionsOptionsComplete,
      permissionsFilter,
      update,

      layoutsOptions,
    };
  },
};
</script>

<style lang="scss">
.td-description {
  max-width: 300px;
}
</style>
